import { useEffect, useRef, useCallback } from 'react';
import { initInsights, trackView } from '@/lib/algolia/insights';
import { recallQueryId } from '@/lib/algolia/query-id-store';
import { useAlgoliaKey } from '@/providers/algolia-key';

/**
 * EWM-809 — PDP Insights. Fires a `viewedObjectIDs` view event once per product
 * view (deduped across remounts of the same sku) and exposes the recalled
 * originating queryID so the add-to-cart conversion can be search-attributed.
 * objectID === sku in the product index.
 */
const usePdpInsights = (sku: string, index: string) => {
  // Read the secured key from context (render-time) and init here, so a cold
  // first render doesn't depend on the provider's effect having run first.
  const { securedApiKey } = useAlgoliaKey();
  const viewedSkuRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!sku || viewedSkuRef.current === sku) return;
    initInsights(securedApiKey);
    // Only mark this sku as viewed if the event actually fired — a cold-start
    // no-op then retries on the next render rather than being suppressed.
    if (trackView({ index, objectIDs: [sku], eventName: 'Product viewed (PDP)' })) {
      viewedSkuRef.current = sku;
    }
  }, [sku, index, securedApiKey]);

  // EWM-809 fix-wave A — return the full attribution object (queryID + index)
  // so the convert event fires against the correct replica index, not always
  // the primary. The caller is responsible for handling undefined (non-search).
  const getSearchAttribution = useCallback(() => recallQueryId(sku) ?? undefined, [sku]);

  return { getSearchAttribution };
};

export default usePdpInsights;
